import { useEffect, useState } from 'react';
import { useStudent } from '../context/StudentContext';
import { recommendApi, rankTableApi, scoresApi, type RecommendResult, type Score, type HighSchool } from '../api';
import { Star, TrendingUp, Target, RefreshCw, ChevronDown, ChevronUp, Heart, X, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function Recommendation() {
  const { currentStudent } = useStudent();
  const [recommendation, setRecommendation] = useState<RecommendResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [manualRank, setManualRank] = useState('');
  const [manualScore, setManualScore] = useState('');
  const [useManualInput, setUseManualInput] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [expandedSchool, setExpandedSchool] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const educationLevel = currentStudent?.education_level || 'senior';
  const isJunior = educationLevel === 'junior';

  useEffect(() => {
    if (!currentStudent) return;
    loadRecommendation();
  }, [currentStudent?.id]);

  const loadRecommendation = async () => {
    if (!currentStudent) return;
    setLoading(true);
    try {
      const res = await recommendApi.byStudent(currentStudent.id);
      setRecommendation(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!currentStudent) return;
    setCalculating(true);
    try {
      let provinceRank: number | undefined;

      if (useManualInput && manualScore) {
        // 根据分数查位次
        const rankRes = await rankTableApi.scoreToRank(parseInt(manualScore));
        provinceRank = rankRes.data.data?.rank;
      } else if (manualRank) {
        provinceRank = parseInt(manualRank);
      }

      const res = await recommendApi.byStudent(currentStudent.id, provinceRank);
      setRecommendation(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setCalculating(false);
    }
  };

  const toggleFavorite = (schoolName: string) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(schoolName)) {
      newFavorites.delete(schoolName);
    } else {
      newFavorites.add(schoolName);
    }
    setFavorites(newFavorites);
  };

  const allSchools = [
    ...(recommendation?.chong || []).map(s => ({ ...s, category: '冲刺' as const })),
    ...(recommendation?.wen || []).map(s => ({ ...s, category: '稳妥' as const })),
    ...(recommendation?.bao || []).map(s => ({ ...s, category: '保底' as const })),
  ];

  const categoryColors: Record<string, { bg: string; border: string; text: string; badge: string }> = {
    '冲刺': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-500' },
    '稳妥': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-500' },
    '保底': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500' },
  };

  // 判断是否为高中推荐数据（中考）
  const isHighSchool = (s: any): s is HighSchool => 'name' in s && 'score_line' in s;

  return (
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">智能推荐</h1>
        <p className="text-gray-500 text-sm mt-1">
          {isJunior ? '基于成绩和排名智能推荐冲稳保三档高中' : '基于成绩和排名智能推荐冲稳保三档院校'}
        </p>
      </div>

      {/* 输入区域 */}
      <div className="bg-white rounded-2xl p-4 md:p-5 shadow-sm border border-gray-100 mb-4 md:mb-6">
        <div className="flex flex-wrap items-end gap-4">
          {/* 切换输入方式 */}
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setUseManualInput(false)}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                !useManualInput ? 'bg-white text-blue-600 font-medium shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              使用成绩数据
            </button>
            <button
              onClick={() => setUseManualInput(true)}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                useManualInput ? 'bg-white text-blue-600 font-medium shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              手动输入
            </button>
          </div>

          {!useManualInput ? (
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                基于 <span className="font-medium text-gray-800">{currentStudent?.name}</span> 的最新成绩自动计算
              </p>
            </div>
          ) : (
            <div className="flex-1 flex items-center gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {isJunior ? '预估分数' : '预测省位次'}
                </label>
                <input
                  type="number"
                  value={isJunior ? manualScore : manualRank}
                  onChange={e => isJunior ? setManualScore(e.target.value) : setManualRank(e.target.value)}
                  placeholder={isJunior ? '如：650' : '如：5500'}
                  className="w-32 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              {!isJunior && (
                <>
                  <span className="text-gray-400">或</span>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">高考成绩</label>
                    <input
                      type="number"
                      value={manualScore}
                      onChange={e => setManualScore(e.target.value)}
                      placeholder="如：580"
                      className="w-32 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <button
            onClick={handleCalculate}
            disabled={calculating}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={calculating ? 'animate-spin' : ''} />
            {calculating ? '计算中...' : '重新计算'}
          </button>
        </div>

        {/* 当前预测信息 */}
        {recommendation && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4 flex-wrap">
            {recommendation.province_rank != null && (
              <div className="flex items-center gap-2">
                <Target size={16} className="text-blue-500" />
                <span className="text-sm text-gray-600">
                  {isJunior ? '预估位次' : '预测省位次'}：
                </span>
                <span className="text-lg font-bold text-blue-600">第 {recommendation.province_rank?.toLocaleString()} 名</span>
              </div>
            )}
            {(recommendation.score || recommendation.student_score) && (
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-green-500" />
                <span className="text-sm text-gray-600">参考分数：</span>
                <span className="text-lg font-bold text-green-600">
                  {recommendation.student_score || recommendation.score} 分
                </span>
              </div>
            )}
            {recommendation.year && (
              <span className="text-xs text-gray-400">基于 {recommendation.year} 年数据</span>
            )}
          </div>
        )}

        {/* 当前使用的偏好设置 */}
        {!isJunior && recommendation?.preferences_used && (
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-500">当前偏好：</span>
            {recommendation.preferences_used.target_cities?.length > 0 && (
              <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
                📍 {recommendation.preferences_used.target_cities.join(', ')}
              </span>
            )}
            {recommendation.preferences_used.school_type_preference && (
              <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded-full">
                🎓 {recommendation.preferences_used.school_type_preference}
              </span>
            )}
            {recommendation.preferences_used.exclude_cities?.length > 0 && (
              <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-full">
                🚫 排除: {recommendation.preferences_used.exclude_cities.join(', ')}
              </span>
            )}
            {(!recommendation.preferences_used.target_cities?.length &&
              !recommendation.preferences_used.school_type_preference &&
              !recommendation.preferences_used.exclude_cities?.length) && (
              <span className="text-xs text-gray-400">未设置偏好，使用全部院校</span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        /* 加载状态 */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : recommendation ? (
        <>
          {/* 统计概览 */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
            {[
              { label: isJunior ? '冲刺高中' : '冲刺院校', count: recommendation.chong?.length || 0, color: 'amber' },
              { label: isJunior ? '稳妥高中' : '稳妥院校', count: recommendation.wen?.length || 0, color: 'green' },
              { label: isJunior ? '保底高中' : '保底院校', count: recommendation.bao?.length || 0, color: 'blue' },
            ].map(item => (
              <div key={item.label} className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2 h-2 rounded-full bg-${item.color}-500`}></div>
                  <span className="text-sm text-gray-500">{item.label}</span>
                </div>
                <div className={`text-2xl font-bold text-${item.color}-600`}>{item.count}</div>
              </div>
            ))}
          </div>

          {/* 中考模式：统招/定向生分组统计 */}
          {isJunior && recommendation.zhao_sheng_count !== undefined && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Info size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">招生类型分布</span>
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-gray-600">统招</span>
                  <span className="font-semibold text-gray-800">{recommendation.zhao_sheng_count}所</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                  <span className="text-sm text-gray-600">定向生</span>
                  <span className="font-semibold text-gray-800">{recommendation.ding_xiang_count}所</span>
                </div>
              </div>
            </div>
          )}

          {/* 院校/高中列表 */}
          {allSchools.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center text-gray-400">
              <Star size={48} className="opacity-30 mx-auto mb-4" />
              <p>{recommendation.message || '暂无推荐数据，请先录入成绩'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {['冲刺', '稳妥', '保底'].map(category => {
                const schools = allSchools.filter(s => s.category === category);
                if (schools.length === 0) return null;

                const colors = categoryColors[category];

                return (
                  <div key={category} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 ${colors.badge} rounded-lg flex items-center justify-center text-white font-bold text-sm`}>
                        {category[0]}
                      </div>
                      <h3 className={`font-semibold ${colors.text}`}>{category}</h3>
                      <span className="text-xs text-gray-400">({schools.length}所)</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {schools.map((school, idx) => {
                        const isHS = isHighSchool(school);

                        return (
                          <div
                            key={idx}
                            className={`${colors.bg} ${colors.border} border rounded-xl p-4 hover:shadow-md transition-shadow`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-gray-800 truncate">
                                    {isHS ? (school as HighSchool).name : (school as any).school_name}
                                  </h4>
                                  {/* 高考标签：985/211 */}
                                  {!isHS && (school as any).is985 === 1 && (
                                    <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded flex-shrink-0">985</span>
                                  )}
                                  {!isHS && (school as any).is985 !== 1 && (school as any).is211 === 1 && (
                                    <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded flex-shrink-0">211</span>
                                  )}
                                  {/* 中考标签：示范/普高/职高 */}
                                  {isHS && (school as HighSchool).is_demonstration === 1 && (
                                    <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded flex-shrink-0">示范</span>
                                  )}
                                  {isHS && (school as HighSchool).school_type === '职高' && (
                                    <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded flex-shrink-0">职高</span>
                                  )}
                                  {isHS && (school as HighSchool).school_type === '普高' && (
                                    <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded flex-shrink-0">普高</span>
                                  )}
                                  {/* 招生类型标签：统招/定向生 */}
                                  {isHS && (school as HighSchool).quota_type === '定向生' && (
                                    <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded flex-shrink-0" title="定向生">定向</span>
                                  )}
                                  {isHS && (school as HighSchool).quota_type === '统招' && (
                                    <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex-shrink-0" title="统招">统招</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <Star size={12} />
                                    {isHS ? (school as HighSchool).district : (school as any).city}
                                  </span>
                                  {isHS ? (
                                    <>
                                      <span>分数线 {(school as HighSchool).score_line}</span>
                                      {(school as HighSchool).rank_line && (
                                        <span>位次 {(school as HighSchool).rank_line?.toLocaleString()}</span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <span>均分 {(school as any).avg_score}</span>
                                      <span>均位次 {(school as any).avg_rank?.toLocaleString()}</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <button
                                onClick={() => toggleFavorite(isHS ? (school as HighSchool).name : (school as any).school_name)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  favorites.has(isHS ? (school as HighSchool).name : (school as any).school_name)
                                    ? 'text-red-500 bg-red-100'
                                    : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                                }`}
                              >
                                <Heart size={18} fill={favorites.has(isHS ? (school as HighSchool).name : (school as any).school_name) ? 'currentColor' : 'none'} />
                              </button>
                            </div>

                            {/* 偏好匹配原因（仅高考） */}
                            {!isHS && (school as any).match_reasons && (school as any).match_reasons.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {(school as any).match_reasons.map((reason: string, i: number) => (
                                  <span key={i} className="text-xs bg-white/70 text-gray-600 px-2 py-0.5 rounded">
                                    {reason}
                                  </span>
                                ))}
                                {(school as any).preference_score > 0 && (
                                  <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded font-medium">
                                    匹配度 +{(school as any).preference_score}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* 历年数据（仅高考） */}
                            {!isHS && (school as any).years && (
                              <div className="mt-3 pt-3 border-t border-gray-200/50">
                                <button
                                  onClick={() => setExpandedSchool(expandedSchool === ((school as any).school_name) ? null : (school as any).school_name)}
                                  className="w-full flex items-center justify-between text-xs text-gray-500 hover:text-gray-700"
                                >
                                  <span>历年录取数据</span>
                                  {expandedSchool === (school as any).school_name ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>

                                {expandedSchool === (school as any).school_name && (school as any).years && (
                                  <div className="mt-2">
                                    <ResponsiveContainer width="100%" height={100}>
                                      <LineChart data={Object.entries((school as any).years as Record<string, unknown>).sort(([a], [b]) => Number(a) - Number(b)).map(([year, data]) => ({ year, ...(data as Record<string, unknown>) }))}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                                        <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                                        <Tooltip formatter={(v: number) => v?.toLocaleString()} />
                                        <Line type="monotone" dataKey="lowest_rank" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                                      </LineChart>
                                    </ResponsiveContainer>
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                      {Object.entries((school as any).years).map(([year, data]: [string, any]) => (
                                        <div key={year} className="text-center">
                                          <div className="font-medium">{year}</div>
                                          <div>{data.lowest_rank?.toLocaleString()}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 录取概率条 */}
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-gray-500">录取概率</span>
                                <span className="font-medium text-gray-700">
                                  {category === '冲刺' ? '30-50%' : category === '稳妥' ? '60-80%' : '90%以上'}
                                </span>
                              </div>
                              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${colors.badge} rounded-full`}
                                  style={{ width: category === '冲刺' ? '40%' : category === '稳妥' ? '70%' : '95%' }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-2xl p-16 text-center text-gray-400">
          <Target size={48} className="opacity-30 mx-auto mb-4" />
          <p>暂无推荐数据</p>
          <p className="text-sm mt-2">请先录入成绩或手动输入预测位次</p>
        </div>
      )}
    </div>
  );
}
